// apps/roleforge-app/src/components/deliverables/ImplementationRecommendations.tsx
// Plan 3b-ii Phase 7.2: Three-bucket view (easy / medium / hard).

import type { Opportunity } from '../../lib/api-client';
import { getEngineColor } from '@vibecontrol/shared/roleforge';

interface ImplementationRecommendationsProps {
  opportunities: Opportunity[];
  engineLabels?: Record<string, string>;
}

type Bucket = 'easy' | 'medium' | 'hard';

const BUCKET_CONFIG: { id: Bucket; label: string; description: string }[] = [
  {
    id: 'easy',
    label: 'Easy Wins',
    description: 'Low-effort, high-value — start here.',
  },
  {
    id: 'medium',
    label: 'Medium Effort',
    description: 'Worthwhile but require planning.',
  },
  {
    id: 'hard',
    label: 'Heavy Lifts',
    description: 'High-impact but resource-intensive.',
  },
];

function OppCard({
  opp,
  engineLabel,
}: {
  opp: Opportunity;
  engineLabel?: string;
}) {
  const color = getEngineColor(opp.engine);
  const fallbackEngineLabel = isUuidLike(opp.engine) ? null : opp.engine.replace(/_/g, ' ');
  const displayEngine = engineLabel ?? fallbackEngineLabel;
  return (
    <div
      className="rounded-md border border-slate-200 dark:border-slate-700 bg-card px-4 py-3 shadow-sm"
      style={{ borderLeftColor: color, borderLeftWidth: 4 }}
    >
      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{opp.name}</p>
      {opp.description && (
        <p className="mt-1 text-xs text-slate-500">{opp.description}</p>
      )}
      <div className="mt-1.5 flex flex-wrap gap-1 text-xs">
        {displayEngine && (
          <span className="rounded bg-slate-100 dark:bg-slate-800/60 px-1.5 py-0.5 text-slate-500 capitalize">
            {displayEngine}
          </span>
        )}
        <span className="rounded bg-slate-100 dark:bg-slate-800/60 px-1.5 py-0.5 text-slate-500">
          Impact: {opp.business_impact}
        </span>
      </div>
    </div>
  );
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  );
}

export function ImplementationRecommendations({
  opportunities,
  engineLabels = {},
}: ImplementationRecommendationsProps) {
  // Only applicable opportunities
  const applicable = opportunities.filter((o) => o.applies);

  return (
    <div className="flex flex-col gap-6">
      {BUCKET_CONFIG.map(({ id: bucket, label, description }) => {
        const items = applicable
          .filter((o) => o.effort === bucket)
          .sort((a, b) => b.business_impact - a.business_impact);

        return (
          <section key={bucket}>
            <div className="mb-2">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</h3>
              <p className="text-xs text-slate-500">{description}</p>
            </div>

            {items.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No items in this bucket.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {items.map((opp) => (
                  <OppCard key={opp.id} opp={opp} engineLabel={engineLabels[opp.engine]} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
